import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Loader2, Utensils, Scissors, Droplets, Slice, ChevronRight } from "lucide-react";

const CATEGORY_META = {
  butchery: { label: "Butchery", icon: Scissors, color: "text-orange-500" },
  boning: { label: "Boning", icon: Slice, color: "text-yellow-400" },
  prep: { label: "Prep", icon: Utensils, color: "text-lime-400" },
  "knife-care": { label: "Knife care", icon: Utensils, color: "text-sky-400" },
  hygiene: { label: "Hygiene", icon: Droplets, color: "text-orange-400" },
};

const CATEGORY_ORDER = ["butchery", "boning", "prep", "knife-care", "hygiene"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Skills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    setLoading(true);
    const url = category === "all" ? "/skills" : `/skills?category=${encodeURIComponent(category)}`;
    api.get(url).then((r) => setSkills(r.data)).catch(() => setSkills([])).finally(() => setLoading(false));
  }, [category]);

  const grouped = CATEGORY_ORDER.map((c) => ({
    category: c,
    items: skills.filter((s) => s.category === c),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="min-h-screen pt-24 pb-32 bg-stone-950">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10">
        <div className="mb-12 border-b border-stone-800 pb-10">
          <div className="flex items-center gap-4 mb-6">
            <span className="magazine-number text-5xl">06</span>
            <div className="w-16 h-px bg-orange-600" />
            <span className="eyebrow">The Skills Room</span>
          </div>
          <h1 className="font-serif-display text-6xl md:text-7xl text-stone-100 leading-[0.9] tracking-tighter">
            Sharp knives, <span className="italic text-orange-600">clean hands</span>.
          </h1>
          <p className="mt-8 text-stone-300 max-w-2xl leading-relaxed">
            Butchery, boning, knife-care, hygiene — the fundamentals every cook wishes they&apos;d learned
            first. Short lessons, no faff, straight from the pass.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          <button
            data-testid="skills-filter-all"
            onClick={() => setCategory("all")}
            className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              category === "all"
                ? "border-orange-600 bg-orange-700/20 text-orange-500"
                : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
            }`}
          >
            Everything
          </button>
          {CATEGORY_ORDER.map((c) => {
            const meta = CATEGORY_META[c];
            const Icon = meta.icon;
            const active = category === c;
            return (
              <button
                key={c}
                data-testid={`skills-filter-${c}`}
                onClick={() => setCategory(c)}
                className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  active
                    ? "border-orange-600 bg-orange-700/20 text-orange-500"
                    : "border-stone-800 text-stone-400 hover:border-stone-600 hover:text-stone-200"
                }`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {meta.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
          </div>
        ) : skills.length === 0 ? (
          <div className="text-center py-32 border border-stone-800">
            <div className="magazine-number text-8xl mb-6">Ø</div>
            <p className="font-serif-display italic text-2xl text-stone-400">
              No lessons in this drawer yet.
            </p>
          </div>
        ) : category === "all" ? (
          <div className="space-y-20">
            {grouped.map((group) => {
              const meta = CATEGORY_META[group.category];
              const Icon = meta.icon;
              return (
                <div key={group.category}>
                  <div className="flex items-center gap-3 mb-8 border-t border-stone-800 pt-6">
                    <Icon className={`w-5 h-5 ${meta.color}`} strokeWidth={1.5} />
                    <span className={`eyebrow ${meta.color}`}>{meta.label}</span>
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {group.items.map((s, i) => <SkillCard key={s.id} skill={s} index={i} />)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((s, i) => <SkillCard key={s.id} skill={s} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export const SkillCard = ({ skill }) => {
  const meta = CATEGORY_META[skill.category] || CATEGORY_META.prep;
  const Icon = meta.icon;
  const heroImg = skill.image_data
    ? `data:image/png;base64,${skill.image_data}`
    : skill.image_status === "ready"
    ? `${BACKEND}/api/skills/${skill.slug}/og_image`
    : null;
  return (
    <Link
      to={`/skills/${skill.slug}`}
      data-testid={`skill-card-${skill.slug}`}
      className="group block border border-stone-800 bg-stone-900/40 hover:border-orange-700 transition-colors"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-stone-900">
        {heroImg ? (
          <img
            src={heroImg}
            alt={skill.title}
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900 flex flex-col items-center justify-center gap-3">
            <Icon className={`w-8 h-8 ${meta.color} opacity-40`} strokeWidth={1.25} />
            <p className="font-serif-display italic text-sm text-stone-500">Illustration on its way</p>
          </div>
        )}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-stone-950/80 backdrop-blur-md px-3 py-1">
          <Icon className={`w-3 h-3 ${meta.color}`} strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.24em] text-stone-200">{meta.label}</span>
        </div>
        {skill.video_id && (
          <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-orange-600 text-stone-950 px-2.5 py-1">
            <span className="w-0 h-0 border-l-[6px] border-l-stone-950 border-y-[4px] border-y-transparent" />
            <span className="text-[9px] uppercase tracking-[0.24em] font-medium">Video</span>
          </div>
        )}
      </div>
      <div className="p-6 space-y-3">
        <h3 className="font-serif-display text-2xl leading-tight text-stone-100 group-hover:text-orange-500 transition-colors">
          {skill.title}
        </h3>
        <p className="text-sm text-stone-400 italic line-clamp-2">{skill.subtitle}</p>
        <div className="flex items-center justify-between pt-3 text-xs text-stone-500 font-mono-accent">
          <span>~{skill.duration_min} min · {skill.difficulty}</span>
          <ChevronRight className="w-4 h-4 group-hover:text-orange-500 transition-colors" />
        </div>
      </div>
    </Link>
  );
};

export default Skills;
